import { makeStyles } from "@material-ui/core/styles";
import { AppList } from "components/list/AppList";
import Tooltip from "@material-ui/core/Tooltip";
import React, { useEffect } from "react";
import {
	FunctionField,
	Loading,
	SelectInput,
	TextField,
	useGetList,
	useRefresh,
	useTranslate,
} from "react-admin";

import { Datagrid } from "../../../components/CustomizableDataGrid/";
import { DateFormatField } from "../../../components/fields/DateFormatField";
import { ReferenceEditableField } from "../../../components/fields/EditableField";
import { TextEditableField } from "../../../components/fields/EditableField";
import { LongTextField } from "../../../components/fields/LongTextField";
import { ObjectChipField } from "../../../components/fields/ObjectChipField";
import { OSFCUserSelectField } from "../../../components/fields/OSFCUserSelect";
import { PriorityField } from "../../../components/fields/PriorityField";
import { ScrollingWrapper } from "../../../components/ScrollingWrapper";
import { SearchResultDocumentsRowExpand } from "../../../components/SearchResultRowExpand";
import GridUserDataProvider, {
	useGridUserDataContext,
} from "../../../contexts/GridUserDataProvider";
import { AppManagerRoles } from "../../../entities/";
import { TaskServices } from "../../../entities/TaskService";
import { isInArray } from "../../../utils/general";
import { useAsyncColumnsStorage } from "../../../utils/hooks/useAsyncColumnsStorage";
import { useExpandDefaultForAll } from "../../../utils/hooks/useExpandDefaultForAll";
import { useIsSearchMode } from "../../../utils/hooks/useIsSearchMode";
import { ActionButtonsField, TasksActions } from "./Actions";
import { TasksFilter, filterDefaultValues } from "./Filters";
import { useGridUserDataGlobalFiltersContext } from "contexts/GridUserDataGlobalFiltersProvider";

const defaultSort = { field: "id", order: "DESC" };

const BG_COLOR_WHITE = "#ffffff";
const BG_COLOR_GREEN = "#b7ff7d";
const BG_COLOR_RED = "#ff8c8c";

const useStyles = makeStyles({
	list: {
		noResults: {
			backgroundColor: "red",
			padding: 100,
		},
		actions: {
			backgroundColor: "blue",
		},
	},
});

const useCellStyles = makeStyles({
	cellGreen: {
		backgroundColor: BG_COLOR_GREEN, // green
	},
	cellRed: {
		backgroundColor: BG_COLOR_RED, // red
	},
	noPaddingButton: {
		padding: "0",

		"&>span>button": {
			backgroundColor: BG_COLOR_WHITE,
			border: "solid 1px white",
			minWidth: "auto",
		},
	},
	noPaddingButtonStart: {
		"&>span>button": {
			borderTopRightRadius: 0,
			borderBottomRightRadius: 0,
			borderRight: "none",
		},
		// '.order-cc-late &>span>button': {
		// 	backgroundColor: BG_COLOR_RED
		// },
	},
	noPaddingButtonEnd: {
		"&>span>button": {
			borderTopLeftRadius: 0,
			borderBottomLeftRadius: 0,
			borderLeft: "none",
		},
		// '.order-cc-late &>span>button': {
		// 	backgroundColor: BG_COLOR_GREEN
		// },
	},
	// cellEtaTime: {
	// 	'.order-cc-late &>span': {
	// 		backgroundColor: BG_COLOR_RED,
	// 		color: BG_COLOR_GREEN,
	// 	},
	// },
	// cellEtdTime: {
	// 	'.order-cc-late &>span': {
	// 		backgroundColor: BG_COLOR_GREEN,
	// 		color: BG_COLOR_RED
	// 	},
	// },
});

const Label = ({ title, label }) => {
	return (
		<Tooltip title={title} placement="bottom">
			<div>{label}</div>
		</Tooltip>
	);
};


export const _TasksList = ({ permissions, ...props }) => {
	const { filters: _currentFilters, sort: currentSort } = useGridUserDataContext();
	const { data: globalFilters = {} } = useGridUserDataGlobalFiltersContext();
	const currentFilters = {..._currentFilters, ...globalFilters};

	const classes = useStyles();
	const cellClasses = useCellStyles();
	const translate = useTranslate();
	const t = (field) => translate(`resources.tasks.fields.${field}`);
	const gridLabel = (field) => translate(`osfc.grid-label.${field}`);
	const isSearchMode = useIsSearchMode(currentFilters, props.resource);
	const columnsAsyncStorage = useAsyncColumnsStorage("tasks-columns");
	const refresh = useRefresh();

	const { ids: _, data: statuses } = useGetList("task-statuses");

	useEffect(() => {
		if (permissions?.time_refresh_order) {
			const intervalId = setInterval(refresh, permissions.time_refresh_order * 1000);
			return () => clearInterval(intervalId);
		}
	}, [permissions, refresh]);

	useExpandDefaultForAll(props.resource);

	if (!permissions || currentFilters === undefined || !columnsAsyncStorage.isLoaded) {
		return <Loading loadingSecondary="" />;
	}

	const orderRowStyle = (record, index) => {
		return {
			backgroundColor: statuses[record.status] && statuses[record.status].color,
		};
	};

	const isAppManager = permissions && isInArray(AppManagerRoles, permissions.role);

	return (
		<AppList
			{...props}
			empty={false}
			classes={classes.list}
			exporter={false}
			filter={currentFilters}
			filters={<TasksFilter isSearchMode={isSearchMode} />}
			filterDefaultValues={currentFilters}
			bulkActionButtons={false}
			sort={currentSort}
			actions={<TasksActions />}
		>
			<ScrollingWrapper>
				<Datagrid
					rowStyle={orderRowStyle}
					storage={columnsAsyncStorage}
					isRowExpandable={() => isSearchMode}
					expand={isSearchMode ? <SearchResultDocumentsRowExpand /> : false}
					expandIconCell={{ padding: "2px" }}
				>
					<ActionButtonsField isSearchMode={isSearchMode} />

					<PriorityField
						source="priority"
						label={<Label title={t("priority")} label={gridLabel("priority")} />}
						sortable={false}
					/>

					<ReferenceEditableField
						optimized={true}
						editable={isAppManager}
						permissions={permissions}
						source="status"
						reference="task-statuses"
						additionalParams={["set_order_completed"]}
						editField={<SelectInput optionText="name" />}
					>
						<TextField source="status_name" />
					</ReferenceEditableField>

					<ObjectChipField
						source="service"
						object={TaskServices}
						titleKey="short_name"
						tooltipKey="full_name"
					/>

					<TextField source="title" />

					<TextEditableField editable={isAppManager} source="rank" />

					<TextField source="rank_auto" />

					<TextField source="terminal_name" label="resources.tasks.fields.terminal" />

					<TextField source="et_date_union" />

					<DateFormatField source="eta_date" headerClassName={cellClasses.cellGreen} />

					<TextField
						source="eta_time"
						headerClassName={cellClasses.cellGreen}
						cellClassName={cellClasses.cellEtaTime}
					/>

					<OSFCUserSelectField
						source="assignee_1"
						visibleSource="assignee_1_name"
						headerClassName={cellClasses.cellGreen}
						permissions={permissions}
					/>
					<DateFormatField source="etd_date" headerClassName={cellClasses.cellRed} />

					<TextField
						source="etd_time"
						headerClassName={cellClasses.cellRed}
						cellClassName={cellClasses.cellEtdTime}
					/>
					<OSFCUserSelectField
						source="assignee_2"
						visibleSource="assignee_2_name"
						headerClassName={cellClasses.cellRed}
						permissions={permissions}
					/>
					<LongTextField source="notes" sortable={false} />
				</Datagrid>
			</ScrollingWrapper>
		</AppList>
	);
};

export const TasksList = (props) => (
	<GridUserDataProvider
		resource={props.resource}
		filterDefaultValues={filterDefaultValues}
		sortDefaultValues={defaultSort}
	>
		<_TasksList {...props} />
	</GridUserDataProvider>
);
